import { designTokens } from "@itandi/itandi-bb-ui";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { InputCheckBox } from "@itandi/itandi-bb-ui/InputCheckBox";
import { Tooltip } from "@itandi/itandi-bb-ui/Tooltip";
import { Controller, useFormContext } from "react-hook-form";
import { ApplicationTypeCheckBox } from "~/components/specifics/deals/index/search/detail/ApplicationTypeCheckBox";
import useItandiAccount from "~/hooks/useItandiAccount";
import { useStatuses } from "~/hooks/useStatuses";
import { useSubtypes } from "~/hooks/useSubtypes";
import type { CompanyVendor } from "~/models/CompanyVendor";
import type { DealSearchCondition } from "~/models/Deal";
import { CompanyStoreCombobox } from "./detail/CompanyStoreCombobox";
import { CompanyVendorCombobox } from "./detail/CompanyVendorCombobox";
import { DealTypeCheckBox } from "./detail/DealTypeCheckBox";
import { SearchConditionDate } from "./detail/SearchConditionDate";
import { StaffCombobox } from "./detail/StaffCombobox";
import { StatusCombobox } from "./detail/StatusCombobox";

type Props = {
	companyVendors: CompanyVendor[];
};

export function DealSearchDetailTab({ companyVendors }: Props) {
	const { data: subtypes } = useSubtypes().useIndex();
	const { watch, register } = useFormContext<DealSearchCondition>();
	const { data: session } = useItandiAccount().currentAccount();
	const { data: statuses } = useStatuses().index();
	const applicationTypes = watch("applicationTypes") ?? [];
	const hasVendorEstimationRequest =
		applicationTypes.includes("requestEstimation");

	if (subtypes === null || session === null || statuses === null) {
		return null;
	}

	return (
		<FormGroup variant="vertical">
			<FormControl label="未読を優先表示する">
				<Controller
					name="prioritySort"
					render={({ field: { value, onChange, ...field } }) => (
						<InputCheckBox
							{...field}
							checked={!!value}
							label="未読を優先表示する"
							onChange={(e) => {
								onChange(e.target.checked);
							}}
						/>
					)}
				/>
			</FormControl>
			<FormControl label="案件タイプ">
				<Flex marginY={8} wrap="wrap">
					<DealTypeCheckBox
						dealType="restoration"
						key="restoration"
						label="原状回復"
					/>
					<DealTypeCheckBox dealType="repair" key="repair" label="修繕" />
					{subtypes.map((subtype) => (
						<DealTypeCheckBox
							dealSubtypeId={subtype.id}
							dealType="other"
							key={subtype.id}
							label={subtype.name}
						/>
					))}
				</Flex>
			</FormControl>
			<FormControl label="状況(ステータス)">
				<StatusCombobox statuses={statuses} />
			</FormControl>
			<FormControl label="状況(申請関連)">
				<Flex marginY={8} wrap="wrap">
					<ApplicationTypeCheckBox
						applicationType="cancel"
						key="cancel"
						label="取下申請中"
					/>
					<ApplicationTypeCheckBox
						applicationType="requestEstimationByCompany"
						key="requestEstimationByCompany"
						label="見積申請中"
					/>
					<Flex alignItems="center">
						<Tooltip>
							管理会社にて見積作成を行う案件のうち、見積変更を「申請中」の条件で絞り込みます
						</Tooltip>
					</Flex>
					<ApplicationTypeCheckBox
						applicationType="draftEstimationByCompany"
						key="draftEstimationByCompany"
						label="見積編集中"
					/>
					<Flex alignItems="center">
						<Tooltip>
							管理会社にて見積作成を行う案件のうち、見積変更を「編集中」(未申請)の条件で絞り込みます
						</Tooltip>
					</Flex>
				</Flex>
			</FormControl>
			<FormControl label="施工会社からの見積申請">
				<Flex marginY={8} wrap="wrap">
					<ApplicationTypeCheckBox
						applicationType="requestEstimation"
						label="見積申請あり"
					/>
				</Flex>
			</FormControl>
			<FormControl label="見積申請日">
				<Flex alignItems="center" gap={designTokens["spacing/xs"]}>
					<SearchConditionDate
						attributeName="estimationRequestedMinDate"
						disabled={!hasVendorEstimationRequest}
					/>
					〜
					<SearchConditionDate
						attributeName="estimationRequestedMaxDate"
						disabled={!hasVendorEstimationRequest}
					/>
				</Flex>
				<FormControlText help="「見積申請あり」選択時のみ指定可能です" />
			</FormControl>
			<FormControl label="物件名">
				<Input {...register("name")} placeholder="六本木マンション" />
			</FormControl>
			<FormControl label="部屋番号">
				<Input {...register("roomNumber")} placeholder="101" />
			</FormControl>
			<FormControl label="所在地">
				<Input {...register("address")} placeholder="東京都港区六本木1-2-3" />
			</FormControl>
			<FormControl label="入居者">
				<Input {...register("tenantName")} placeholder="内装 太郎" />
			</FormControl>
			{session.store?.masterAccount ? (
				<FormControl label="店舗">
					<CompanyStoreCombobox />
				</FormControl>
			) : null}
			<FormControl label="担当者">
				<StaffCombobox />
			</FormControl>
			<FormControl label="施工会社">
				<CompanyVendorCombobox companyVendors={companyVendors} />
			</FormControl>
			<FormControl label="更新日">
				<Flex alignItems="center" gap={designTokens["spacing/xs"]}>
					<SearchConditionDate attributeName="updatedMinDate" />
					〜
					<SearchConditionDate attributeName="updatedMaxDate" />
				</Flex>
			</FormControl>
			<FormControl label="立会日程">
				<Flex alignItems="center" gap={designTokens["spacing/xs"]}>
					<SearchConditionDate attributeName="inspectionMinDate" />
					〜
					<SearchConditionDate attributeName="inspectionMaxDate" />
				</Flex>
			</FormControl>
			<FormControl label="現地調査日程">
				<Flex alignItems="center" gap={designTokens["spacing/xs"]}>
					<SearchConditionDate attributeName="surveyMinDate" />
					〜
					<SearchConditionDate attributeName="surveyMaxDate" />
				</Flex>
			</FormControl>
			<FormControl label="見積提出日">
				<Flex alignItems="center" gap={designTokens["spacing/xs"]}>
					<SearchConditionDate attributeName="sendEstimateMinDate" />
					〜
					<SearchConditionDate attributeName="sendEstimateMaxDate" />
				</Flex>
				<FormControlText help="ステータスを「見積確認中」に進めた日" />
			</FormControl>
			<FormControl label="発注日">
				<Flex alignItems="center" gap={designTokens["spacing/xs"]}>
					<SearchConditionDate attributeName="orderMinDate" />
					〜
					<SearchConditionDate attributeName="orderMaxDate" />
				</Flex>
				<FormControlText help="ステータスを「工事完了待ち」に進めた日" />
			</FormControl>
			<FormControl label="工期開始日">
				<Flex alignItems="center" gap={designTokens["spacing/xs"]}>
					<SearchConditionDate attributeName="constructionMinStartDate" />
					〜
					<SearchConditionDate attributeName="constructionMaxStartDate" />
				</Flex>
			</FormControl>
			<FormControl label="工期終了日">
				<Flex alignItems="center" gap={designTokens["spacing/xs"]}>
					<SearchConditionDate attributeName="constructionMinEndDate" />
					〜
					<SearchConditionDate attributeName="constructionMaxEndDate" />
				</Flex>
			</FormControl>
			<FormControl label="検収依頼日">
				<Flex alignItems="center" gap={designTokens["spacing/xs"]}>
					<SearchConditionDate attributeName="constructionReportedMinDate" />
					〜
					<SearchConditionDate attributeName="constructionReportedMaxDate" />
				</Flex>
				<FormControlText help="ステータスを「検収中」に進めた日" />
			</FormControl>
			<FormControl label="完了日">
				<Flex alignItems="center" gap={designTokens["spacing/xs"]}>
					<SearchConditionDate attributeName="completedMinDate" />
					〜
					<SearchConditionDate attributeName="completedMaxDate" />
				</Flex>
				<FormControlText help="ステータスを「完了」に進めた日" />
			</FormControl>
			<FormControl label="登録日">
				<Flex alignItems="center" gap={designTokens["spacing/xs"]}>
					<SearchConditionDate attributeName="registrationMinDate" />
					〜
					<SearchConditionDate attributeName="registrationMaxDate" />
				</Flex>
			</FormControl>
		</FormGroup>
	);
}
